import * as yup from 'yup';
import { FormManager } from 'react-form-state-manager';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBag, emailRegex } from '../../../common/helpers';
import ContactPicker from '../Contacts/ContactPicker';
import TeamPicker from './TeamPicker';
import UI from '../../../common/UI';

const requiredIfPresent = (value: any) => typeof value === 'undefined' || value;

/**
 * Assumes that attributes are required when they are present.
 */
export const findOrAddTeamSchema = yup.object({
  id: yup.string().test('required', '', requiredIfPresent),
  title: yup.string().test('required', '', requiredIfPresent),
  captain: yup.object({
    id: yup.string().test('required', '', requiredIfPresent),
    email: yup.string().test('required', '', requiredIfPresent).matches(emailRegex, { name: 'email' }),
    first_name: yup.string().test('required', '', requiredIfPresent),
    last_name: yup.string().test('required', '', requiredIfPresent),
  }),
});

interface Team {
  id: string;
  title: string;
}

interface Captain {
  id: string;
  full_name: string;
  email: string;
}

export interface FormData {
  team: {
    id?: string;
    title?: string;
    captain?: {
      id?: string;
      email?: string;
      first_name?: string;
      last_name?: string;
    }
  } | null;
}

export interface FindOrAddTeamFormProps {
  form: FormManager<FormData>;
  eventId: string;
  team?: Team | null;
  captain?: Captain | null;
  errors?: ErrorBag;
}

const FindOrAddTeamForm = ({
  form, eventId, team: initialTeam = null, captain: initialCaptain = null, errors,
}: FindOrAddTeamFormProps) => {
  const { t } = useTranslation('backend');

  const [team, setTeam] = useState<Team | null>(initialTeam);
  const [captain, setCaptain] = useState<Captain | null>(initialCaptain);

  const initialTeamMode = initialTeam ? 'existing' : 'none';
  const [teamMode, setTeamMode] = useState<'none' | 'existing' | 'new'>(initialTeamMode);
  const [captainMode, setCaptainMode] = useState<'existing' | 'new'>('existing');

  const initialTeamRef = useRef(initialTeam);

  const reset = useCallback(() => {
    setTeamMode(initialTeamMode);
    setCaptainMode('existing');
    setTeam(initialTeam);
    setCaptain(initialCaptain);
  }, [initialTeamMode, initialTeam, initialCaptain]);

  useEffect(() => {
    // When the initial team changes (e.g., when the form is saved), reset the UI
    if (initialTeamRef.current !== initialTeam) {
      reset();
      form.reset('team');
      initialTeamRef.current = initialTeam;
    }
  }, [reset, initialTeam, form]);

  const deselectTeam = () => {
    reset();
    setTeamMode('none');

    form.set((values) => ({
      ...values,
      team: null,
    }));
  };

  const selectExistingTeam = () => {
    reset();
    setTeamMode('existing');

    form.set((values) => ({
      ...values,
      team: { id: '' },
    }));
  };

  const createNewTeam = () => {
    reset();
    setTeamMode('new');

    form.set((values) => ({
      ...values,
      team: { title: '', captain: { id: initialCaptain?.id || '' }, event: { id: eventId } },
    }));
  };

  const selectTeam = (team: Team | null) => {
    setTeam(team);

    form.set((values) => ({
      ...values,
      team: { id: team?.id || '' },
    }));
  };

  const selectExistingCaptain = () => {
    setCaptainMode('existing');

    form.set((values) => ({
      ...values,
      team: {
        ...values.team,
        captain: { id: '' },
      },
    }));
  };

  const createNewCaptain = () => {
    setCaptainMode('new');

    form.set((values) => ({
      ...values,
      team: {
        ...values.team,
        captain: { email: '', first_name: '', last_name: '' },
      },
    }));
  };

  const selectCaptain = (captain: Captain | null) => {
    setCaptain(captain);

    form.set((values) => ({
      ...values,
      team: {
        ...values.team,
        captain: { id: captain?.id || '' },
      },
    }));
  };

  return (
    <>
      <UI.InputGroup>
        <UI.InputLabel htmlFor="team_picker_no_team">
          {t('team')}
        </UI.InputLabel>
        <UI.Radio
          checked={teamMode === 'none'}
          onChange={() => deselectTeam()}
          id="team_picker_no_team"
        >
          {t('no_team')}
        </UI.Radio>
        <UI.Radio
          checked={teamMode === 'existing'}
          onChange={() => selectExistingTeam()}
        >
          {t('select_an_existing_team')}
        </UI.Radio>
        <UI.Radio
          checked={teamMode === 'new'}
          onChange={() => createNewTeam()}
        >
          {t('create_a_new_team')}
        </UI.Radio>
      </UI.InputGroup>

      {teamMode === 'existing' && (
        <UI.InputGroup sc={{ valid: !errors?.['team.id'], touched: form.getTouched('team.id') }}>
          <UI.InputLabel>
            {t('existing_team')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <TeamPicker
            team={team}
            onChange={selectTeam}
            filters={{ event: eventId }}
            nullable={false}
          />
          <UI.ErrorMessages attribute={t('team')} errors={errors?.team} />
        </UI.InputGroup>
      )}

      {teamMode === 'new' && (
        <>
          <UI.InputGroup sc={{ valid: !errors?.['team.title'], touched: form.getTouched('team.title') }}>
            <UI.InputLabel htmlFor="TeamName">
              {t('team_name')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <UI.DebouncedInput {...form.text('team.title')} id="TeamName" />
            <UI.ErrorMessages attribute={t('team_name')} errors={errors?.['team.title']} />
          </UI.InputGroup>

          {!initialCaptain && (
            <>
              <UI.InputGroup>
                <UI.Radio
                  checked={captainMode === 'existing'}
                  onChange={() => selectExistingCaptain()}
                >
                  {t('select_an_existing_captain')}
                </UI.Radio>
                <UI.Radio
                  checked={captainMode === 'new'}
                  onChange={() => createNewCaptain()}
                >
                  {t('create_a_new_captain')}
                </UI.Radio>
              </UI.InputGroup>

              {captainMode === 'new' && (
                <>
                  <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
                    <UI.InputGroup
                      sc={{
                        valid: !errors?.['team.captain.first_name'],
                        touched: form.getTouched('team.captain.first_name'),
                      }}
                    >
                      <UI.InputLabel htmlFor="FirstName">
                        {t('first_name')}
                        {' '}
                        <UI.RequiredMark />
                      </UI.InputLabel>
                      <UI.DebouncedInput {...form.text('team.captain.first_name')} id="FirstName" />
                      <UI.ErrorMessages attribute={t('first_name')} errors={errors?.['team.captain.first_name']} />
                    </UI.InputGroup>
                    <UI.InputGroup
                      sc={{
                        valid: !errors?.['team.captain.last_name'], touched: form.getTouched('team.captain.last_name'),
                      }}
                    >
                      <UI.InputLabel htmlFor="LastName">
                        {t('last_name')}
                        {' '}
                        <UI.RequiredMark />
                      </UI.InputLabel>
                      <UI.DebouncedInput {...form.text('team.captain.last_name')} id="LastName" />
                      <UI.ErrorMessages attribute={t('last_name')} errors={errors?.['team.captain.last_name']} />
                    </UI.InputGroup>
                  </UI.FormGrid>
                  <UI.InputGroup
                    sc={{ valid: !errors?.['team.captain.email'], touched: form.getTouched('team.captain.email') }}
                  >
                    <UI.InputLabel htmlFor="Email">
                      {t('email_address')}
                      {' '}
                      <UI.RequiredMark />
                    </UI.InputLabel>
                    <UI.DebouncedInput {...form.text('team.captain.email')} id="Email" />
                    <UI.ErrorMessages attribute={t('email')} errors={errors?.['team.captain.email']} />
                  </UI.InputGroup>
                </>
              )}

              {captainMode === 'existing' && (
                <UI.InputGroup
                  sc={{ valid: !errors?.['team.captain'], touched: form.getTouched('team.captain') }}
                >
                  <UI.InputLabel>
                    {t('captain')}
                    {' '}
                    <UI.RequiredMark />
                  </UI.InputLabel>
                  <ContactPicker participant={captain} onChange={selectCaptain} nullable={false} />
                  <UI.ErrorMessages attribute={t('captain')} errors={errors?.['team.captain']} />
                </UI.InputGroup>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default FindOrAddTeamForm;
